import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { useBoxProgramSlotDefaultSettings } from "@/modules/boxProgrammation/hooks/useBoxProgramSlotDefaultSettings"
import { BoxSlotFormikEntity } from "@/modules/boxSlot/entities/boxSlotFormikEntity"
import { getBoxSlotValidationSchema } from "@/modules/boxSlot/formik/boxSlotFormikTypes"
import { useBoxSlotDetails } from "@/modules/boxSlot/hooks/useBoxSlotDetails"
import { useBoxSlotsCreate, useBoxSlotsUpdate } from "@/modules/boxSlot/hooks/useBoxSlots"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { word } from "@modules/core/utils/i18n"
import { useFormik } from "formik"

export const useBoxSlotFormikUpdate = (boxSlotId: string) => {
  const { data: boxSlot } = useBoxSlotDetails(boxSlotId)
  const { mutateAsync: updateSlot } = useBoxSlotsUpdate()
  const { hideSidebar } = useSidebar()

  const formik = useFormik<BoxSlotFormikEntity>({
    initialValues: BoxSlotFormikEntity.fromEntity(boxSlot),
    validationSchema: getBoxSlotValidationSchema(),
    enableReinitialize: true,
    onSubmit: async () => {
      try {
        await updateSlot({ slotId: boxSlotId, slotInput: formik.values.toInputDto() })
        toast.success(word("box.slot.form.toast.update.success"), {
          classNames: successToastClassName(),
        })
        hideSidebar()
      } catch (e: unknown) {
        if (typeof e === "object" && e !== null && "message" in e && typeof e.message === "string") {
          toast.error(word("global.error.default"), {
            description: e.message,
            classNames: errorToastClassName(),
          })
        }
      }
    },
  })

  return formik
}

export const useBoxSlotFormikCreate = (boxProgramId: string, startDate: Date) => {
  const { data: defaultSettings } = useBoxProgramSlotDefaultSettings(boxProgramId)
  const { mutateAsync: createSlot } = useBoxSlotsCreate()
  const { hideSidebar } = useSidebar()

  const formik = useFormik<BoxSlotFormikEntity>({
    initialValues: BoxSlotFormikEntity.fromDefaultSettings(boxProgramId, startDate, defaultSettings),
    validationSchema: getBoxSlotValidationSchema(),
    enableReinitialize: true,
    onSubmit: async () => {
      try {
        await createSlot(formik.values.toInputDto())
        toast.success(word("box.slot.form.toast.creation.success"), {
          classNames: successToastClassName(),
        })
        hideSidebar()
      } catch (e: unknown) {
        if (typeof e === "object" && e !== null && "message" in e && typeof e.message === "string") {
          toast.error(word("global.error.default"), {
            description: e.message,
            classNames: errorToastClassName(),
          })
        }
      }
    },
  })

  return formik
}
